// Benchmark data loading and processing utilities

export interface BenchmarkError {
  temperature: number;
  pressure: number;
  absoluteError: number;
  relativeError: number | null;
  category: string;
  testType: string;
  testPoint: {
    pressureUnit?: string;
    [key: string]: any;
  };
}

export interface BenchmarkSummary {
  refrigerant: string;
  totalTests: number;
  averageError: number;
  maxError: number;
  accuracyDistribution: {
    excellent: number;
    good: number;
    acceptable: number;
    poor: number;
    unacceptable: number;
  };
}

export interface BenchmarkData {
  refrigerant: string;
  summary: BenchmarkSummary;
  errors: BenchmarkError[];
}

// Load benchmark data from the JSON file
export async function loadBenchmarkData(): Promise<BenchmarkData[]> {
  try {
    const response = await fetch('/comprehensive-detailed-benchmarks.json');
    if (!response.ok) {
      throw new Error(`Failed to load benchmark data: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    return data.results.map((result: any) => ({
      refrigerant: result.refrigerant,
      summary: result.summary,
      errors: result.detailedResults.map((test: any) => ({
        temperature: test.testPoint.temperature,
        pressure: test.testPoint.pressure,
        absoluteError: test.error.absolute,
        relativeError: test.error.relative,
        category: test.error.category,
        testType: test.metadata.testType,
        testPoint: test.testPoint
      }))
    }));
  } catch (error) {
    console.error('Error loading benchmark data:', error);
    return [];
  }
}

// Get benchmark data for a specific refrigerant
export function getBenchmarkDataForRefrigerant(
  benchmarkData: BenchmarkData[], 
  refrigerantCode: string
): BenchmarkData | null {
  // Map refrigerant codes to benchmark names
  const refrigerantMap: { [key: string]: string } = {
    'R134a': 'R-134a',
    'R32': 'R-32',
    'R22': 'R-22',
    'R410A': 'R-410A',
    'R404A': 'R-404A',
    'R407C': 'R-407C',
    'R1234yf': 'R-1234yf',
    'R744': 'R-744',
    'R290': 'R-290'
  };
  
  const benchmarkName = refrigerantMap[refrigerantCode];
  if (!benchmarkName) return null;
  
  return benchmarkData.find(data => data.refrigerant === benchmarkName) || null;
}

// Get color for error category
export function getErrorCategoryColor(category: string): string {
  switch (category) {
    case 'excellent':
      return '#4CAF50'; // Green
    case 'good':
      return '#8BC34A'; // Light green
    case 'acceptable':
      return '#FFC107'; // Yellow
    case 'poor':
      return '#FF9800'; // Orange
    case 'unacceptable':
      return '#F44336'; // Red
    default:
      return '#9E9E9E'; // Gray
  }
} 